/**
 * A ranked breakdown, drawn in CSS rather than SVG.
 *
 * SVG is for the curve; bars are rectangles that reflow. Doing them in CSS means
 * they theme themselves through the palette, need no `useColor`, and stay legible
 * at any width without a viewBox.
 */

import { EmptyState, RADIUS } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

export interface BreakdownRow {
  key: string;
  label: string;
  value: number;
  /** Shown to the right of the label, already formatted. */
  caption?: string;
}

export interface BreakdownBarsProps {
  title: string;
  rows: BreakdownRow[];
  emptyMessage: string;
  formatValue: (value: number) => string;
  testId?: string;
}

export function BreakdownBars({
  title,
  rows,
  emptyMessage,
  formatValue,
  testId,
}: BreakdownBarsProps) {
  const max = Math.max(1, ...rows.map((row) => row.value));

  return (
    <Box data-testid={testId}>
      <Typography variant="H3" sx={{ marginBottom: 2 }}>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <EmptyState message={emptyMessage} />
      ) : (
        <Stack
          spacing={1.5}
          component="ul"
          sx={{ listStyle: "none", margin: 0, padding: 0 }}
        >
          {rows.map((row) => (
            <Box
              component="li"
              key={row.key}
              data-testid={`${testId}-${row.key}`}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ justifyContent: "space-between", alignItems: "baseline" }}
              >
                <Typography variant="T3">{row.label}</Typography>
                <Typography
                  variant="L2"
                  sx={{
                    color: "text.muted",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {row.caption ?? formatValue(row.value)}
                </Typography>
              </Stack>
              <Box
                sx={{
                  marginTop: 0.5,
                  height: 8,
                  borderRadius: `${RADIUS.full}px`,
                  backgroundColor: "surface.inset",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${(row.value / max) * 100}%`,
                    height: "100%",
                    borderRadius: `${RADIUS.full}px`,
                    backgroundColor: "primary.main",
                  }}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
